import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import CancelActionButton from 'components/CancelActionButton';
import { CheckboxSc } from 'components/final-form/CheckBox';
import DatePicker from 'components/final-form/DatePicker';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  mapToDropdownOptionArrayWithSep
} from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import RadioGroup from 'components/final-form/RadioGroup';
import {
  generateRadioButtonOptionsFromEnum,
  generateRadioButtonOptionsFromEnumAndPrefix
} from 'components/final-form/radioUtils';
import Select from 'components/final-form/Select';
import TextArea from 'components/final-form/TextArea';
import TimeInput from 'components/final-form/TimeInput';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import {
  PatientCentralRefDataConsumerRenderProps,
  withPatientCentralRefDataContext
} from 'components/patient-entry/PatientCentralRefDataContext';
import SaveActionButton from 'components/SaveActionButton';
import { FormApi } from 'final-form';
import createDecorator from 'final-form-calculate';
import _ from 'lodash';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Dimmer, Grid, Icon, Loader, Popup, RadioProps } from 'semantic-ui-react';
import axios from 'services/http';
import {
  getOrderItemResult,
  setControlledByBookingStatus,
  setOrderItemResult,
  setResultRejectedBookingStatus,
  verifyTestPanelResult
} from 'services/interventionBookingServices';
import styled from 'styled-components';
import {
  CovidTestSampleMethod,
  InterventionBookingStatus,
  InterventionBookingTestResult,
  InterventionExternalCode,
  LabTestResultType
} from 'ts-types/api.enums';
import {
  InterventionDto,
  InterventionOrderItemResultDto,
  InterventionTestBookingSearchResult,
  LabTestComponentResultDto,
  LabTestPanelResultDto,
  TestResultUpdateResponseDto,
  UpsertOrderItemResultDto
} from 'ts-types/api.types';
import { isoToDisplayDate } from 'utils/dateUtils';
import { errorUtils } from 'utils/errorUtils';
import { noop } from 'utils/functionUtils';
import { getSampleMethods } from 'utils/interventionUtils';
import { composeValidators, ctValidator, mocValidator, required } from 'utils/validatorUtils';

const StyledButton = styled.button`
  margin-right: -0.3rem;
  padding-right: 0;
  border: none;
  color: #2185d0;
  background: none;
  cursor: pointer;

  .button:hover {
    cursor: pointer;
  }
`

const StyledDiv = styled.div`
  .clickable:hover {
    cursor: pointer;
  }
`

const PopupStyle = {
  borderRadius: 5,
  opacity: 0.9,
  padding: '7px'
};

const StyledForm = styled.form`
  padding: 0.75rem;
  width: 100%;

  .patient-data {
    font-size: 1.1rem;
    font-weight: 500;

    .barcode {
      color: #7a97ff;
    }
  }

  .field-label {
    margin-top: 0.4rem;
    margin-bottom: 0.7rem;

    .inline-span {
      display: inline-block;
      margin-right: 1rem;
    }
  }

  .field-label:not(:first-child) {
  }

  .multi-field-row {
    display: flex;
    flex-direction: row;

    & > div {
      margin-right: 1.5rem;
    }

    .date-input {
      width: 8rem;
    }

    .time-input .ui.mini.input {
      width: 6rem;
    }
  }

  .radio-group .ui.radio.checkbox {
    margin-right: 1.5rem;
  }

  .row-result {
    height: 2.5rem;
  }

  & .ui.grid {
    padding-left: 0 !important;
  }

  .field-error {
    color: darkred;
    font-size: 1rem;
  }

  .loader-box {
    position: relative;
    width: 300px;
    height: 90px;
  }

  .test-result-warning {
    color: #b58105;
    margin-top: 0.5rem;
    font-size: 1rem;
  }

  .test-result-error {
    color: darkred;
    margin-top: 0.5rem;
    font-size: 1rem;
  }

  .previous-test-results {
    width: 100%;
    border-top: 1px solid #c5c5c5;
    padding-top: 0.5rem;

    .no-results-message {
      font-style: italic;
      color: grey
    }
  }

  .text-result-value {
    .field {
      width: 100%;
    }
  }

  .actions-row {
    margin-top: 2rem;

    button {
      margin-right: 1rem;
    }
  }
`;

interface UpsertOrderItemResultFormDto extends UpsertOrderItemResultDto {
  testRegistrationDate: string;
  testRegistrationTime: string;
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps, PatientCentralRefDataConsumerRenderProps {
  interventionBooking: InterventionTestBookingSearchResult,
  onClose: () => void,
  onCloseAndUpdate: (bookingId: number) => void
}

enum ModalState {
  SET_RESULT,
  VERIFY,
  CONTROL
}

interface State {
  requiredDataLoaded: boolean,
  modalState: ModalState,
  componentRadioPropsMap: { [id: string]: RadioProps[] }
  testSampleMethodProps: RadioProps[],
  labOptions: DropdownOption[],
  analyzerOptions: DropdownOption[],
  testIntervention?: InterventionExternalCode,
  includingRevoked: boolean,
  modifySampleDateTime: boolean,
  testResultInProgress: boolean,
  testResultSaveError?: string,
  testResultSaveWarning?: string,
  orderItemResultPanels?: LabTestPanelResultDto[],
  errorMessage?: string,
  displayCommentInterpretation: boolean
}

class SetTestPanelResultModal extends Component<Props, State> {

  cancelTokenSource = axios.CancelToken.source();

  private initialValues: Partial<UpsertOrderItemResultFormDto>;

  interventionMap: { [id: number]: InterventionDto } = {};

  constructor(props: Props) {
    super(props);

    const booking = props.interventionBooking;

    this.initialValues = {
      testSampleMethod: booking.testSampleMethod,
      testRegistrationDate: booking.testRegistrationDate,
      testRegistrationTime: booking.testRegistrationTime,
      laboratoryId: booking.laboratoryId,
      analyzerId: booking.analyzerId
    };

    let testIntervention = undefined;
    const intervention = props.getInterventionById(booking.interventionId);
    if (intervention) {
      testIntervention = InterventionExternalCode[intervention.externalCode];
    }

    this.interventionMap = _.keyBy(this.props.interventions, "id");

    let sampleMethods = testIntervention ? getSampleMethods(testIntervention) : [];
    if (intervention && !_.isNil(intervention.sampleMethods)) {
      sampleMethods = intervention!.sampleMethods.map(sm => CovidTestSampleMethod[sm]);
    }

    const testSampleMethodProps = generateRadioButtonOptionsFromEnum(
        sampleMethods, "covidTestSampleMethod", props.getMessage);

    const filteredAnalyzers = booking.laboratoryId
        ? props.covidTestAnalyzers.filter(analyzer => analyzer.laboratoryId === booking.laboratoryId)
        : props.covidTestAnalyzers;

    const analyzerOptions: DropdownOption[] =
        mapToDropdownOptionArrayWithSep(filteredAnalyzers, ["code", "description"], "id");

    let modalState = ModalState.SET_RESULT;
    if (InterventionBookingStatus.RESULTED === booking.status && !booking.testResultVerified) {
      modalState = ModalState.VERIFY;
    } else if (InterventionBookingStatus.VERIFIED === booking.status && booking.testResultVerified) {
      modalState = ModalState.CONTROL;
    }

    this.state = {
      requiredDataLoaded: false,
      modalState,
      componentRadioPropsMap: {},
      testSampleMethodProps: testSampleMethodProps,
      labOptions: mapToDropdownOptionArray(props.laboratories, "name"),
      analyzerOptions,
      includingRevoked: false,
      testIntervention: testIntervention,
      modifySampleDateTime: false,
      testResultInProgress: false,
      displayCommentInterpretation: false
    };

    this.fetchRequiredData(props);
  }

  fetchRequiredData = async (props: Props) => {

    const covidTestId = props.interventionBooking.testId;

    const orderItemResult = await getOrderItemResult(covidTestId, this.cancelTokenSource);

    if (orderItemResult) {
      this.initialValues = {
        ...this.initialValues,
        result: orderItemResult
      };

      this.setState({
        orderItemResultPanels: orderItemResult.components
      });

      const componentRadioPropsMap: { [id: string]: RadioProps[] } = {};

      orderItemResult.components.forEach(orderItemPanel => {

        const components: LabTestComponentResultDto[] = orderItemPanel.components;

        components.forEach(component => {
          if (LabTestResultType.POSITIVE_NEGATIVE_CT === component.resultType) {

            const testResults = [
              InterventionBookingTestResult.POSITIVE,
              InterventionBookingTestResult.NEGATIVE
            ];

            componentRadioPropsMap[component.code] = generateRadioButtonOptionsFromEnumAndPrefix(
                testResults, component.code, "enum.testResult", props.getMessage);

          } else if (LabTestResultType.POSITIVE_NEGATIVE_UNCERTAIN_CT === component.resultType) {

            const testResults = [
              InterventionBookingTestResult.POSITIVE,
              InterventionBookingTestResult.NEGATIVE,
              InterventionBookingTestResult.UNCERTAIN];

            componentRadioPropsMap[component.code] = generateRadioButtonOptionsFromEnumAndPrefix(
                testResults, component.code, "enum.testResult", props.getMessage);
          } else if (LabTestResultType.POSITIVE_NEGATIVE_BORDERLINE_MOC === component.resultType) {

            const testResults = [
              InterventionBookingTestResult.POSITIVE,
              InterventionBookingTestResult.NEGATIVE,
              InterventionBookingTestResult.BORDERLINE];

            componentRadioPropsMap[component.code] = generateRadioButtonOptionsFromEnumAndPrefix(
                testResults, component.code, "enum.testResult", props.getMessage);
          }
        });
      });

      this.setState({
        componentRadioPropsMap
      });
    }

    this.setState({
      requiredDataLoaded: true
    });
  }

  setTestResult = async (
      form: FormApi,
      interventionBooking: InterventionTestBookingSearchResult,
      values: Partial<UpsertOrderItemResultFormDto>) => {

    if (form.getState().invalid) {
      return;
    }

    const {
      testRegistrationDate,
      testRegistrationTime
    } = values;

    this.setState({
      testResultInProgress: true
    });

    const registrationTimestamp = this.state.modifySampleDateTime
        ? `${testRegistrationDate}T${testRegistrationTime}`
        : undefined;

    const testPanelResultDto: UpsertOrderItemResultDto = {
      ...values,
      treaterId: this.props.options.currentTreater.id,
      registrationTimeStamp: registrationTimestamp
    }

    try {
      let resultUpdateDto: TestResultUpdateResponseDto;
      resultUpdateDto = await setOrderItemResult(
          interventionBooking.bookingId, testPanelResultDto, this.cancelTokenSource);

      this.setState({
        testResultInProgress: false
      });

      if (resultUpdateDto.certificateError) {
        this.setState({
          testResultSaveWarning:
              "patientCentral.overview.testResult.warn.officialCertificateNotCreated"
        });

        return;
      }

    } catch (e) {
      this.setState({
        testResultInProgress: false,
        testResultSaveError: "patientCentral.overview.testResult.error.general"
      });
      this.handleError(e.response.data);
      return;
    }

    this.closeAndUpdate();
  };

  verifyTestResult = async (
      interventionBooking: InterventionTestBookingSearchResult) => {

    this.setState({
      testResultInProgress: true
    });

    try {
      await verifyTestPanelResult(interventionBooking.bookingId, this.cancelTokenSource);

      this.setState({
        testResultInProgress: false
      });

    } catch (e) {
      this.setState({
        testResultInProgress: false,
        testResultSaveError: "patientCentral.overview.testResult.error.general"
      });
      this.handleError(e.response.data);
      return;
    }

    this.closeAndUpdate();
  }

  setTestResultInProgress = (testResultInProgress: boolean) => {
    this.setState({
      testResultInProgress
    })
  }

  setControlledBy = async () => {
    const {interventionBooking, options} = this.props;

    this.setTestResultInProgress(true);

    const bookingId = interventionBooking.bookingId;
    const treaterId = options.currentTreater.id;

    try {
      const response = await setControlledByBookingStatus(bookingId, treaterId, this.cancelTokenSource);
      if (response.certificateError) {
        this.setState({
          testResultSaveWarning:
              "patientCentral.overview.testResult.warn.officialCertificateNotCreated"
        });

        return;
      }
    } catch (e) {
      this.setState({
        testResultInProgress: false,
        testResultSaveError: "patientCentral.overview.testResult.error.general"
      });
      this.handleError(e.response.data);
      return;
    }

    this.closeAndUpdate();
  };

  setResultRejected = async () => {
    const {interventionBooking, options} = this.props;

    this.setTestResultInProgress(true);

    const bookingId = interventionBooking.bookingId;
    const treaterId = options.currentTreater.id;

    try {
      const response = await setResultRejectedBookingStatus(bookingId, treaterId, this.cancelTokenSource);
    } catch (e) {
      this.setState({
        testResultInProgress: false,
        testResultSaveError: "patientCentral.overview.testResult.error.general"
      });
      this.handleError(e.response.data);
      return;
    }

    this.closeAndUpdate();
  };

  handleError(error: any) {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors = [
      errorUtils.testBookingWithCanceledStatus
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  closeAndUpdate = () => {
    this.props.onCloseAndUpdate(this.props.interventionBooking.bookingId);
  };

  parseDecimalInput = (value: any) => {
    if (!value || value.length < 6) {
      return value;
    } else {
      return value.slice(0, 5);
    }
  };

  changeCovidTestAnalyzerOptions = createDecorator({
    field: 'laboratoryId',
    updates: {
      analyzerId: (fieldValue, allValues?: Partial<InterventionTestBookingSearchResult>) => {
        if (fieldValue) {
          const filteredAnalyzers =
              this.props.covidTestAnalyzers.filter(analyzer => analyzer.laboratoryId === fieldValue);

          const analyzerOptions: DropdownOption[] =
              mapToDropdownOptionArrayWithSep(filteredAnalyzers, ["code", "description"], "id");

          this.setState({
            analyzerOptions
          });

          if (filteredAnalyzers.length) {
            const filteredAnalyzerIds: number[] = _.map(filteredAnalyzers, 'id');
            if (!allValues
                || !allValues.analyzerId
                || (allValues.analyzerId && !filteredAnalyzerIds.includes(allValues.analyzerId))
            ) {

              return filteredAnalyzers[0].id;
            } else {
              return allValues.analyzerId;
            }
          }
        }

        return undefined;
      }
    }
  });

  onSavePanelCommentInterpretation = (orderItemPanel: LabTestPanelResultDto, orderItemPanelIndex: number) => {
    const {orderItemResultPanels} = this.state

    const newOrderItemResultPanels = orderItemResultPanels;
    if (newOrderItemResultPanels) {
      newOrderItemResultPanels[orderItemPanelIndex] = orderItemPanel
    }

    this.setState({
      orderItemResultPanels: newOrderItemResultPanels
    });
  }

  onSaveComponentCommentInterpretation = (
      component: LabTestComponentResultDto, componentIx: number, orderItemPanelIndex: number) => {
    const {orderItemResultPanels} = this.state

    const newOrderItemResultPanels = orderItemResultPanels;
    if (newOrderItemResultPanels) {
      newOrderItemResultPanels[orderItemPanelIndex].components[componentIx] = component;
    }

    this.setState({
      orderItemResultPanels: newOrderItemResultPanels
    });
  }

  render(): React.ReactNode {

    if (!this.state.requiredDataLoaded) {
      return <div className="loader-box">
        <Loader active/>
      </div>;
    }

    return (
        <FinalForm
            onSubmit={noop}
            initialValues={this.initialValues}
            decorators={[this.changeCovidTestAnalyzerOptions]}
            subscription={{values: true, pristine: true, submitting: true, errors: true, error: true}}
            render={this.renderForm}
        />
    );
  };

  renderForm = (
      {values, form, handleSubmit, submitting, errors}: FormRenderProps): React.ReactNode => {

    const {interventionBooking, getMessage} = this.props;
    const {
      modalState,
      testSampleMethodProps,
      errorMessage,
      testIntervention,
      orderItemResultPanels,
      displayCommentInterpretation
    } = this.state;

    const hideSampleMethod = testSampleMethodProps.length === 1
        && testSampleMethodProps[0].value === "NONE";

    const result = values.result as InterventionOrderItemResultDto;
    let fullTestResult = false;

    result.components.forEach(orderItemPanel => {
      const components: LabTestComponentResultDto[] = orderItemPanel.components;

      fullTestResult = components
          && components.filter(component => _.isNil(component.detectedResult)).length === 0;

      fullTestResult = (testIntervention === InterventionExternalCode.QUANTITY_TEST
          || testIntervention === InterventionExternalCode.ORDER_ITEM_TEST)
          ? true
          : fullTestResult;
    });

    if (!this.state.requiredDataLoaded) {
      return <div className="loader-box">
        <Dimmer active={true}>
          <Loader inverted inline="centered" size="medium">
            {getMessage("patientCentral.loader.message")}
          </Loader>
        </Dimmer>
      </div>;
    }

    const setResultState = ModalState.SET_RESULT === modalState;
    const verifyState = ModalState.VERIFY === modalState;
    const controlState = ModalState.CONTROL === modalState;

    const resultReadonly = !setResultState;

    let mappedParamRows: any = [];
    result.components.forEach((
        orderItemPanel, orderItemComponentIx) => {

      const panelResult =
          orderItemResultPanels && orderItemResultPanels[orderItemComponentIx];

      const orderItemPanelComment = panelResult && panelResult.comment;
      const orderItemPanelInterpretation = panelResult && panelResult.interpretation;
      const orderItemComponents = panelResult && panelResult.components;

      const disabledOrderItem = resultReadonly
          || orderItemPanel.hasComment || orderItemPanel.hasInterpretation;
      const orderItemIconColor: string = disabledOrderItem ? '#c7c9c9' : '#008db7'

      const components: LabTestComponentResultDto[] = orderItemPanel.components;

      // @ts-ignore
      const mappedParamRow = components.map((component, componentIx) => {

        const isQuantityDecimal = component.resultType === LabTestResultType.QUANTITY_DECIMAL;

        const panelPrefix = `result.components[${orderItemComponentIx}]`;
        const propertyPrefix = `${panelPrefix}.components[${componentIx}]`;

        const componentResult = orderItemComponents && orderItemComponents[componentIx];
        const componentComment = componentResult && componentResult.comment
        const componentInterpretation = componentResult && componentResult.interpretation

        const disabledComponent = resultReadonly
            || component.hasComment || component.hasInterpretation;

        const componentIconColor = disabledComponent ? '#c7c9c9' : '#008db7';

        return (
            <React.Fragment key={component.code}>
              {
                  componentIx === 0 &&
                  <>
                    <Grid.Column width={6} verticalAlign="middle">
                      <b style={{marginTop: "5px", fontSize: "large"}}>{getMessage(`${orderItemPanel.name}`)}</b>
                      <div style={{display: 'inline-flex'}}>
                        <StyledButton
                            style={{color: `${orderItemIconColor}`}}
                            disabled={disabledOrderItem}
                            onClick={() => {
                              form.change(`${panelPrefix}.hasComment`, true)
                              form.change(`${panelPrefix}.hasInterpretation`, true)
                            }}>
                          <Popup
                              trigger={<Icon
                                  name={orderItemPanelComment ? "comment alternate" : "comment alternate outline"}
                              />}
                              content={`${getMessage("synopsisReport.modal.comment")}`}
                              size="small"
                              position="top center"
                              style={PopupStyle}
                              inverted
                          />
                        </StyledButton>
                        <StyledButton
                            style={{color: `${orderItemIconColor}`}}
                            disabled={disabledOrderItem}
                            onClick={() => {
                              form.change(`${panelPrefix}.hasComment`, true)
                              form.change(`${panelPrefix}.hasInterpretation`, true)
                            }}>
                          <Popup
                              trigger={<Icon
                                  name={orderItemPanelInterpretation ? "sticky note" : "sticky note outline"}
                              />}
                              content={`${getMessage("labTestComponent.interpretation")}`}
                              size="small"
                              position="top center"
                              style={PopupStyle}
                              inverted
                          />
                        </StyledButton>
                      </div>
                    </Grid.Column>
                    {
                      orderItemPanel.hasComment
                          ?
                          <Grid.Row>
                            <Grid.Column width={6} verticalAlign={'middle'}>
                            <span style={{marginLeft: "10px"}}>
                              {getMessage("synopsisReport.modal.comment")}:
                            </span>
                            </Grid.Column>
                            <Grid.Column width={8} verticalAlign="middle" className="text-result-value">
                              <StyledDiv>
                                <Field
                                    fluid
                                    name={`${panelPrefix}.comment`}
                                    component={TextArea}
                                    rows={2}
                                />
                              </StyledDiv>
                              {
                                  Object.keys(errors).includes(`${panelPrefix}.comment`) &&
                                  <div className="field-error">
                                    {getMessage(`patientCentral.testResult.commentError`)}
                                  </div>
                              }
                            </Grid.Column>
                          </Grid.Row>

                          : (orderItemPanelComment && displayCommentInterpretation) &&
                          <Grid.Row>
                            <Grid.Column width={6} verticalAlign={'middle'}>
                            <span style={{marginLeft: "10px"}}>
                              {getMessage("synopsisReport.modal.comment")}:
                            </span>
                            </Grid.Column>
                            <Grid.Column width={8} verticalAlign="middle" className="text-result-value">
                              <p>{orderItemPanelComment}</p>
                              {
                                  Object.keys(errors).includes(`${panelPrefix}.comment`) &&
                                  <div className="field-error">
                                    {getMessage(`patientCentral.testResult.commentError`)}
                                  </div>
                              }
                            </Grid.Column>
                          </Grid.Row>
                    }

                    {
                      orderItemPanel.hasInterpretation
                          ?
                          <Grid.Row>
                            <Grid.Column width={6} verticalAlign={'middle'}>
                              <span style={{marginLeft: "10px"}}>
                                {getMessage("labTestComponent.interpretation")}:
                              </span>
                            </Grid.Column>
                            <Grid.Column width={8} verticalAlign="middle" className="text-result-value">
                              <StyledDiv>
                                <Field
                                    fluid
                                    name={`${panelPrefix}.interpretation`}
                                    component={TextArea}
                                    rows={2}
                                />
                              </StyledDiv>
                              {
                                  Object.keys(errors).includes(`${panelPrefix}.interpretation`) &&
                                  <div className="field-error">
                                    {getMessage(`patientCentral.testResult.interpretationError`)}
                                  </div>
                              }
                            </Grid.Column>
                          </Grid.Row>

                          : (orderItemPanelInterpretation && displayCommentInterpretation) &&
                          <Grid.Row>
                            <Grid.Column width={6} verticalAlign={'middle'}>
                              <span style={{marginLeft: "10px"}}>
                                {getMessage("labTestComponent.interpretation")}:
                              </span>
                            </Grid.Column>
                            <Grid.Column width={8} verticalAlign="middle" className="text-result-value">
                              <p>
                                {orderItemPanelInterpretation}
                              </p>
                              {
                                  Object.keys(errors).includes(`${panelPrefix}.interpretation`) &&
                                  <div className="field-error">
                                    {getMessage(`patientCentral.testResult.interpretationError`)}
                                  </div>
                              }
                            </Grid.Column>
                          </Grid.Row>
                    }
                    {
                        orderItemPanel.hasComment && orderItemPanel.hasInterpretation &&
                        <Grid.Row>
                          <Grid.Column width={6} verticalAlign={'middle'}>

                          </Grid.Column>
                          <Grid.Column width={8} verticalAlign={'middle'}>
                            <SaveActionButton
                                hoverMessage={getMessage('button.save')}
                                onConfirm={() => {
                                  this.onSavePanelCommentInterpretation(orderItemPanel, orderItemComponentIx)
                                  form.change(`${panelPrefix}.hasComment`, false)
                                  form.change(`${panelPrefix}.hasInterpretation`, false)
                                }}
                            />
                            <CancelActionButton
                                hoverMessage={getMessage('button.cancel')}
                                onConfirm={() => {
                                  form.change(`${panelPrefix}.comment`, orderItemPanelComment);
                                  form.change(`${panelPrefix}.interpretation`, orderItemPanelInterpretation);
                                  form.change(`${panelPrefix}.hasComment`, false)
                                  form.change(`${panelPrefix}.hasInterpretation`, false)
                                }}
                            />
                          </Grid.Column>
                        </Grid.Row>
                    }
                  </>
              }

              <Grid.Row
                  key={componentIx}
                  // className={isQuantityDecimal ? "row-result" : ""}
              >
                <Grid.Column width={6} verticalAlign="middle">
                  <span>
                    {component.name}
                  </span>
                  <div style={{display: 'inline-block'}}>
                    <StyledButton
                        style={{color: `${componentIconColor}`}}
                        disabled={disabledComponent}
                        onClick={() => {
                          form.change(`${propertyPrefix}.hasComment`, true)
                          form.change(`${propertyPrefix}.hasInterpretation`, true)
                        }}
                    >
                      <Popup
                          trigger={<Icon
                              name={componentComment ? "comment alternate" : "comment alternate outline"}
                          />}
                          content={`${getMessage("synopsisReport.modal.comment")}`}
                          size="small"
                          position="top center"
                          style={PopupStyle}
                          inverted
                      />
                    </StyledButton>
                    <StyledButton
                        style={{color: `${componentIconColor}`}}
                        disabled={disabledComponent}
                        onClick={() => {
                          form.change(`${propertyPrefix}.hasComment`, true)
                          form.change(`${propertyPrefix}.hasInterpretation`, true)
                        }}
                    >
                      <Popup
                          trigger={<Icon
                              name={componentInterpretation ? "sticky note" : "sticky note outline"}
                          />}
                          content={`${getMessage("labTestComponent.interpretation")}`}
                          size="small"
                          position="top center"
                          style={PopupStyle}
                          inverted
                      />
                    </StyledButton>
                  </div>

                </Grid.Column>
                {
                    component.resultType.startsWith("POSITIVE_NEGATIVE") &&
                    <Grid.Column width={isQuantityDecimal ? 5 : 6} className="radio-group" verticalAlign="middle">
                      <Field
                          name={`${propertyPrefix}.detectedResult`}
                          fluid
                          radiowidth="auto"
                          component={RadioGroup}
                          radioDefinitions={this.state.componentRadioPropsMap[component.code]}
                          validate={required}
                          disabled={resultReadonly}
                      />
                    </Grid.Column>
                }

                {
                    component.detectedResult === 'POSITIVE'
                    && component.resultType.endsWith("_CT") &&
                    <Grid.Column width={3} verticalAlign="middle">
                      <div className="field-label">
                        {getMessage("patientCentral.testResult.ct")}:
                      </div>

                      <Field
                          fluid
                          name={`${propertyPrefix}.ctValue`}
                          component={Input}
                          placeholder="##.##"
                          style={{width: "4rem"}}
                          validate={ctValidator}
                          parse={this.parseDecimalInput}
                          disabled={resultReadonly}
                      />
                      {
                          Object.keys(errors).includes(`${propertyPrefix}.ctValue`) &&
                          <div className="field-error">
                            {getMessage(`patientCentral.testResult.ctError`)}
                          </div>
                      }
                    </Grid.Column>
                }

                {
                    component.resultType === LabTestResultType.POSITIVE_NEGATIVE_BORDERLINE_MOC &&
                    <Grid.Column width={3} verticalAlign="middle">
                      <div className="field-label">
                        {getMessage("patientCentral.testResult.moc")}:
                      </div>

                      <Field
                          fluid
                          name={`${propertyPrefix}.mocValue`}
                          component={Input}
                          placeholder="##.##"
                          style={{width: "4rem"}}
                          validate={composeValidators(required, mocValidator)}
                          parse={this.parseDecimalInput}
                          disabled={resultReadonly}
                      />
                      {
                          Object.keys(errors).includes(`${propertyPrefix}.mocValue`) &&
                          <div className="field-error">
                            {getMessage(`patientCentral.testResult.mocError`)}
                          </div>
                      }
                    </Grid.Column>
                }

                {
                    component.resultType === LabTestResultType.QUANTITY_DECIMAL &&
                    <Grid.Column width={4} verticalAlign="middle">
                      <div style={{display: "inline-flex", alignItems: "baseline"}}>
                        <Field
                            fluid
                            name={`${propertyPrefix}.quantity`}
                            component={Input}
                            style={{width: "4rem"}}
                            disabled={resultReadonly}
                        />
                        <span style={{paddingLeft: "10px"}}>
                          {component.exampleUnits}
                        </span>
                      </div>
                      {
                          Object.keys(errors).includes(`${propertyPrefix}.quantity`) &&
                          <div className="field-error">
                            {getMessage(`patientCentral.testResult.quantityError`)}
                          </div>
                      }
                    </Grid.Column>
                }

                {
                    component.resultType === LabTestResultType.TEXT &&
                    <Grid.Column width={8} verticalAlign="middle" className="text-result-value">
                      <div style={{display: "inline-flex", alignItems: "baseline", width: "100%"}}>
                        <Field
                            fluid
                            name={`${propertyPrefix}.text`}
                            component={TextArea}
                            rows={2}
                            disabled={resultReadonly}
                        />
                        <span style={{paddingLeft: "10px"}}>
                          {component.exampleUnits}
                        </span>
                      </div>
                      {
                          Object.keys(errors).includes(`${propertyPrefix}.text`) &&
                          <div className="field-error">
                            {getMessage(`patientCentral.testResult.quantityError`)}
                          </div>
                      }
                    </Grid.Column>
                }
              </Grid.Row>
              {
                component.hasComment
                    ?
                    <Grid.Row>
                      <Grid.Column width={6} verticalAlign={'middle'}>
                        <span style={{marginLeft: "10px"}}>
                          {getMessage("synopsisReport.modal.comment")}:
                        </span>
                      </Grid.Column>
                      <Grid.Column width={8} verticalAlign="middle" className="text-result-value">
                        <StyledDiv>
                          <Field
                              fluid
                              name={`${propertyPrefix}.comment`}
                              component={TextArea}
                              rows={2}
                          />
                        </StyledDiv>
                        {
                            Object.keys(errors).includes(`${propertyPrefix}.comment`) &&
                            <div className="field-error">
                              {getMessage(`patientCentral.testResult.commentError`)}
                            </div>
                        }
                      </Grid.Column>
                    </Grid.Row>

                    : (componentComment && displayCommentInterpretation) &&
                    <Grid.Row>
                      <Grid.Column width={6} verticalAlign={'middle'}>
                        <span style={{marginLeft: "10px"}}>
                          {getMessage("synopsisReport.modal.comment")}:
                        </span>
                      </Grid.Column>
                      <Grid.Column width={8} verticalAlign="middle" className="text-result-value">
                        <p>
                          {componentComment}
                        </p>
                        {
                            Object.keys(errors).includes(`${propertyPrefix}.comment`) &&
                            <div className="field-error">
                              {getMessage(`patientCentral.testResult.commentError`)}
                            </div>
                        }
                      </Grid.Column>
                    </Grid.Row>
              }

              {
                component.hasInterpretation ? <Grid.Row>
                  <Grid.Column width={6} verticalAlign={'middle'}>
                    <span style={{marginLeft: "10px"}}>
                      {getMessage("labTestComponent.interpretation")}:
                    </span>
                  </Grid.Column>
                  <Grid.Column width={8} verticalAlign="middle" className="text-result-value">
                    <StyledDiv>
                      <Field
                          fluid
                          name={`${propertyPrefix}.interpretation`}
                          component={TextArea}
                          rows={2}
                      />
                    </StyledDiv>
                    {
                        Object.keys(errors).includes(`${propertyPrefix}.interpretation`) &&
                        <div className="field-error">
                          {getMessage(`patientCentral.testResult.interpretationError`)}
                        </div>
                    }
                  </Grid.Column>
                </Grid.Row> : (componentInterpretation && displayCommentInterpretation) && <Grid.Row>
                  <Grid.Column width={6} verticalAlign={'middle'}>
                      <span style={{marginLeft: "10px"}}>
                      {getMessage("labTestComponent.interpretation")}:
                        </span>
                  </Grid.Column>
                  <Grid.Column width={8} verticalAlign="middle" className="text-result-value">
                    <p>
                      {componentInterpretation}
                    </p>
                    {
                        Object.keys(errors).includes(`${propertyPrefix}.interpretation`) &&
                        <div className="field-error">
                          {getMessage(`patientCentral.testResult.interpretationError`)}
                        </div>
                    }
                  </Grid.Column>
                </Grid.Row>
              }
              {
                  component.hasComment && component.hasInterpretation &&
                  <Grid.Row>
                    <Grid.Column width={6}>

                    </Grid.Column>
                    <Grid.Column width={8} verticalAlign="middle">
                      <SaveActionButton
                          hoverMessage={getMessage('button.save')}
                          onConfirm={() => {
                            this.onSaveComponentCommentInterpretation(component, componentIx, orderItemComponentIx)
                            form.change(`${propertyPrefix}.hasComment`, false)
                            form.change(`${propertyPrefix}.hasInterpretation`, false)
                          }}
                      />

                      <CancelActionButton
                          hoverMessage={getMessage('button.cancel')}
                          onConfirm={() => {
                            form.change(`${propertyPrefix}.comment`, componentComment);
                            form.change(`${propertyPrefix}.interpretation`, componentInterpretation);
                            form.change(`${propertyPrefix}.hasComment`, false)
                            form.change(`${propertyPrefix}.hasInterpretation`, false)
                          }}
                      />
                    </Grid.Column>
                  </Grid.Row>
              }
            </React.Fragment>
        )
      });

      mappedParamRows.push(mappedParamRow);
    });

    return <StyledForm onSubmit={handleSubmit}>
      <FormContainer>

        <div className="patient-data">
          <div style={{float: 'right'}}>
            <Button
                basic
                compact
                size="small"
                onClick={() => {
                  this.setState({
                    displayCommentInterpretation: !displayCommentInterpretation
                  })
                }}
            >
              {displayCommentInterpretation
                  ? <><Icon
                      name={"minus"}/> {getMessage("patientCentral.testResult.hideCommentsInterpretations")}</>
                  : <><Icon
                      name={"plus"}/> {getMessage("patientCentral.testResult.showCommentsInterpretations")}</>
              }
            </Button>
          </div>
          {
            interventionBooking.patientId
                ? <div>
                  {`${interventionBooking.patientFirstName} ${interventionBooking.patientLastName}, ${isoToDisplayDate(interventionBooking.patientBirthDate)}`}
                </div>
                : <div>
                  {`${interventionBooking.poolCompanyDescription}`}
                </div>
          }
          <div className="barcode">
            {interventionBooking.testKitCode ? interventionBooking.testKitCode : ""}
          </div>
        </div>
        {
            !this.state.testResultSaveWarning && !this.state.testResultSaveError &&
            <PraxFormGrid>

              {mappedParamRows}

              <Grid.Row>
                <Grid.Column width={8}>
                  <div className="field-label">
                    <span className="inline-span">{getMessage("patientCentral.testResult.sampleDate")}:</span>
                    {
                        setResultState &&
                        <span className="inline-span">
                      <CheckboxSc
                          checked={this.state.modifySampleDateTime}
                          label={getMessage("patientCentral.testResult.modifySampleDate")}
                          onChange={() => {
                            this.setState({modifySampleDateTime: !this.state.modifySampleDateTime})
                          }}
                      />
                    </span>
                    }
                  </div>

                  <div className="multi-field-row">
                    <div className="date-input">
                      <Field
                          name="testRegistrationDate"
                          component={DatePicker}
                          disabled={!this.state.modifySampleDateTime}
                      />
                    </div>

                    <div className="time-input">
                      <Field
                          name="testRegistrationTime"
                          size="mini"
                          component={TimeInput}
                          disabled={!this.state.modifySampleDateTime}
                      />
                    </div>
                  </div>
                </Grid.Column>
              </Grid.Row>

              {
                  !hideSampleMethod &&
                  <Grid.Row>
                    <Grid.Column width={16} className="radio-group">
                      <div className="field-label">
                        <span className="inline-span">{getMessage("testSampleMethod")}:</span>
                      </div>
                      <Field
                          name="testSampleMethod"
                          component={RadioGroup}
                          radioDefinitions={testSampleMethodProps}
                          fluid
                          radiowidth="auto"
                          disabled={resultReadonly}
                      />
                    </Grid.Column>
                  </Grid.Row>
              }

              <Grid.Row>
                <Grid.Column width={6}>
                  <div className="field-label">
                    <span className="inline-span">{getMessage("patientCentral.testResult.laboratory")}:</span>
                  </div>
                  <Field
                      name="laboratoryId"
                      component={Select}
                      options={this.state.labOptions}
                      fluid
                      disabled={resultReadonly}
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={6}>
                  <div className="field-label">
                    <span className="inline-span">{getMessage("patientCentral.testResult.analyzer")}:</span>
                  </div>
                  <Field
                      name="analyzerId"
                      component={Select}
                      options={this.state.analyzerOptions}
                      fluid
                      disabled={resultReadonly}
                  />
                </Grid.Column>
              </Grid.Row>
            </PraxFormGrid>
        }

        {
            this.state.testResultSaveWarning &&
            <div className="test-result-warning">
              {getMessage(this.state.testResultSaveWarning)}
            </div>
        }

        {
            this.state.errorMessage &&
            <StyledErrorMessage>{this.state.errorMessage}</StyledErrorMessage>
        }

        <FormButtonsRow>
          {
              verifyState && !this.state.testResultSaveWarning &&
              <Button
                  compact
                  primary
                  onClick={() => {
                    this.setState({modalState: ModalState.SET_RESULT});
                  }}
                  disabled={this.state.testResultInProgress}
              >
                {getMessage("patientCentral.overview.changeTestResult.button")}
              </Button>
          }

          {
              verifyState && !this.state.testResultSaveWarning &&
              <Button
                  compact
                  primary
                  onClick={() => {
                    this.verifyTestResult(interventionBooking);
                  }}
                  disabled={this.state.testResultInProgress}
              >
                {getMessage("patientCentral.overview.verifyTestResult.button")}
              </Button>
          }

          {
              setResultState && !this.state.testResultSaveWarning &&
              <Button
                  compact
                  primary
                  disabled={form.getState().invalid || !fullTestResult || this.state.testResultInProgress}
                  onClick={() => {
                    if (!fullTestResult) {
                      return;
                    }
                    this.setTestResult(form, interventionBooking, values);
                  }}
              >
                {getMessage("button.save")}
              </Button>
          }

          {
              controlState && !this.state.testResultSaveWarning &&
              <Button
                  compact
                  primary
                  disabled={!fullTestResult || this.state.testResultInProgress || !!errorMessage}
                  onClick={() => {
                    if (!fullTestResult) {
                      return;
                    }
                    this.setControlledBy();
                  }}
              >
                {getMessage("button.confirm")}
              </Button>
          }

          {
              controlState && !this.state.testResultSaveWarning &&
              <Button
                  compact
                  primary
                  disabled={!fullTestResult || this.state.testResultInProgress || !!errorMessage}
                  onClick={() => {
                    if (!fullTestResult) {
                      return;
                    }
                    this.setResultRejected();
                  }}
              >
                {getMessage("button.reject")}
              </Button>
          }

          <Button compact type="button"
                  disabled={submitting}
                  onClick={() => {

                    const {testResultSaveWarning} = this.state;

                    if (testResultSaveWarning) {
                      this.closeAndUpdate();
                    } else {
                      this.props.onClose();
                    }
                  }}
          >
            {getMessage("action.back")}
          </Button>
        </FormButtonsRow>
      </FormContainer>
    </StyledForm>;
  }

}

export default withMessages(withAuthContext(withPatientCentralRefDataContext(SetTestPanelResultModal)));
